/* eslint-disable react-hooks/rules-of-hooks */
import React, { Dispatch, useCallback, useEffect, useMemo, useState, useContext } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { DataPolicyTaskDetails } from "./data-policy-task-details";
import { DocumentPolicyTaskDetails } from "./document-policy-task-details";
import { completeTaskInJourney, updateEntityDraft } from '../../services/fenergo-service';
import { getUserJourneyTasks } from '../../services/bff-service';
import NeoWrapper from "../neo-wrapper/neo-wrapper";
import { useDispatch } from "react-redux";
import { ManualTaskDetails } from "./manual-task-details";
import { setCodatState, setCurrentStepperIndex, setSnackBar } from '../../stores/ui/ui-action';
import { useInitialJourneyTasks, useLatestJourneyTasks, useSelectedEntity, useEntityState } from "../../stores/entity/entity.hooks";
import { fetchCodatConnectionInformation, fetchEntityDraft } from "../../stores/async/async-actions";
import { setJourneyTasksLoadInProgress, setLatestJourneyTasks } from "../../stores/entity/entity-action";
import ROUTES from "../../utils/routes";
import { filterJourneyTasks, getJourneyFromSlug, getTaskFromSlug, getTaskFromId } from "../../utils/fenergo-utils";
import { getMenuOptions } from "../../utils/fenergo-utils";
import CONSTANTS from "../../utils/constants";
import { DataPolicyCompanyDetails } from "./data-policy-company-details/data-policy-company-details";
import { JourneyWrapper } from "../journey-wrapper";
import FENERGO_CONSTANTS from "../../utils/fenergo-constants";
import { useCodatState, useCurrentStepperIndex } from "../../stores/ui/ui.hooks";
import { extractCodatResponse } from "../../utils/codat-utils";
import { StatusCheckpointDetails } from "./status-checkpoint-details/status-checkpoint-details";
import { ESignDetails } from "./e-sign/eSign-details";
import { FeatureToggleContext } from "../../config/toggle.context";
import { JourneyInfo } from "../../interfaces/JourneyInfo.interface";
import { getFormDataValuesObjectForDraftApi } from "../custom/forms/form-helper-methods";

interface GenericTaskDetailsProps { }

export const GenericTaskDetails: React.FC<GenericTaskDetailsProps> = () => {
  const { useToggle, toggles } = useContext(FeatureToggleContext);
  const toggleReadableUrl = useToggle("ftReadableUrlPaths");
  const taskParams = useParams<{journeyId: string; taskId: string;} | {journeyName: string; taskName: string;}>();
  const codatState = useCodatState();

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch: Dispatch<any> = useDispatch();

  const initialJourneyTasks = useInitialJourneyTasks();
  const latestJourneyTasks = useLatestJourneyTasks();
  const selectedEntity = useSelectedEntity();
  const activeStepperIndex = useCurrentStepperIndex();

  const [readOnly, setReadOnly] = useState<boolean>(false);
  const [currentTask, setCurrentTask] = useState<any>();
  const [currentJourney, setCurrentJourney] = useState<JourneyInfo>({journeyId: null, journeyStatus: null, journeyType: null});
  const { journeyId = null, taskId, journeyName, taskName } = toggleReadableUrl(taskParams, {...taskParams ,...currentJourney}) ?? {journeyId: null};
  const {
    entitiesLoadInProgress, entities, entityDraft, entityDraftLoadInProgress,
    journeyTasksLoadInProgress
  } = useEntityState();

  
  const loadNewTasks = (jTasks) => {
    const currentTaskIndex = jTasks.findIndex(task => task.id === currentTask.id);
    var index=1;

    const nextTask = jTasks[currentTaskIndex + index];
    loadTasks(nextTask);
    
    dispatch(setJourneyTasksLoadInProgress(false));
  }

  const loadTasks = (nextTask) => {
    if (nextTask) {
      const nextTaskId = nextTask.id;
      setCurrentTask(null);
      dispatch(setJourneyTasksLoadInProgress(false));
      const journeyRoute = toggleReadableUrl(ROUTES.genericTaskDetails(journeyId, nextTaskId), ROUTES.genericTaskDetailsByName(journeyName, nextTask.name));
      navigate(journeyRoute);
    } else {
      navigate(ROUTES.thankYou);
    }
  };

  const loadPreviousTasks = async () => {
    await getUserJourneyTasks(journeyId).then(jTasks => {
      jTasks = filterJourneyTasks(jTasks);
      const currentTaskIndex = jTasks.findIndex(task => task.id === currentTask.id);
      var index=1;
      const nextTask = jTasks[currentTaskIndex - index];
      loadTasks(nextTask);
      dispatch(setJourneyTasksLoadInProgress(false));
    });
  }

  const completeCurrentTask= async (journeyId: string, taskId: string, status:string) => {
    if (status === CONSTANTS.TASK_IN_PROGRESS){
      await completeTaskInJourney(journeyId, taskId)
    }
  }

  const completeTaskForJourney = async () => {
    try {
      //get task data
      const taskToComplete = await getUserJourneyTasks(journeyId).then(jTasks => {
        jTasks = filterJourneyTasks(jTasks)
        const currentTaskIndex = jTasks.findIndex(task => task.id === currentTask.id);
        return jTasks[currentTaskIndex];
      });
      
      await completeCurrentTask(journeyId, taskToComplete.id, taskToComplete.status);

      dispatch(setJourneyTasksLoadInProgress(true));

      // Fetch new draft of selected entity
      dispatch(fetchEntityDraft(selectedEntity.id, journeyId));
      
      // get updated Task data
      await getUserJourneyTasks(journeyId).then(jTasks => {
        jTasks = filterJourneyTasks(jTasks)
        dispatch(setLatestJourneyTasks(jTasks));
        loadNewTasks(jTasks);
      });

    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while calling API', description: error.message, messageType: 'error' }));
      dispatch(setJourneyTasksLoadInProgress(false));
    }
  };

  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }

  const query = useQuery();
  const platform = useMemo(() => extractCodatResponse(query), [query]);
  
  const showErrors = useCallback(
    (platform) => {
      switch (platform.statusCode) {
        case '403':
          dispatch(setSnackBar({ show: true, message: 'Add Data Source cancelled.', messageType: "error" }));
          break;
        case '500':
          dispatch(setSnackBar({ show: true, message: "We weren't able to connect. Try again or upload your documents manually.", messageType: "error" }));
          break;
        case '501':
          dispatch(setSnackBar({ show: true, message: 'Platform not supported.', messageType: "error" }));
          break;
        default:
          break;
      }
    }, [dispatch]
  )

  useEffect(() => {
    async function savePlatformData() {
      const values = {
        codatCompanyId: codatState?.companyId,
        platformName: codatState?.platformName
      };
      const formData = getFormDataValuesObjectForDraftApi(CONSTANTS.CLIENT, values, entityDraft?.jurisdictions, CONSTANTS.COMPANY, currentTask?.businessCategory);
      try {
        await updateEntityDraft(selectedEntity.id, entityDraft.id, formData);
      } catch(e) {
        console.log("error: ", e);
      }
    }
    if(codatState?.platformName && codatState?.companyId && entityDraft?.id && selectedEntity?.id && currentTask) {
      savePlatformData();
    }
  }, [codatState, selectedEntity, entityDraft, currentTask]);

  useEffect(() => {
    if (platform.integration && platform.integration === 'codat') {
      dispatch(setCodatState(platform));
      dispatch(setCurrentStepperIndex(platform.step));
      showErrors(platform);
    } else {
      dispatch(fetchCodatConnectionInformation());
    }
  },[platform, dispatch, showErrors, selectedEntity])

  useEffect(() => {
    // if (currentTask && currentTask.status !== CONSTANTS.TASK_IN_PROGRESS)
    //   setReadOnly(true);

    // Get current task based on URL parameter when journey instance is available
    const urlTask = toggleReadableUrl(getTaskFromId(fetchJourneyTasks(), taskId), getTaskFromSlug(fetchJourneyTasks(), taskName));
    if (urlTask) {
      setCurrentTask(urlTask);
    }

    if(selectedEntity && journeyName){
      const urlJourney = getJourneyFromSlug(selectedEntity.journeys, journeyName);
      setCurrentJourney(urlJourney);

    }
    // Fetch Entity Draft when Entity ID is available from Journey Instance API
    if (!entityDraftLoadInProgress && selectedEntity && !entityDraft)
      dispatch(fetchEntityDraft(selectedEntity.id, journeyId));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, entityDraftLoadInProgress, entityDraft, initialJourneyTasks]);


  const getTaskDetailsPage = () => {
    if (
      entitiesLoadInProgress ||
      journeyTasksLoadInProgress ||
      entityDraftLoadInProgress
    )
      return null;

    if (
      currentTask &&
      currentTask["taskType"] === "PolicyTask" &&
      currentTask["policyRequirementType"] === "Data" &&
      currentTask["name"] === "Company Details"
    )
      return (
        <DataPolicyCompanyDetails
          journeyId={journeyId}
          journeyName={journeyName}
          entityDraft={entityDraft}
          currentTask={currentTask}
          readOnly={readOnly}
          completeTask={completeTaskForJourney}
        />
      );


    if (
      (currentTask &&
        currentTask["taskType"] === "PolicyTask" &&
        currentTask["policyRequirementType"] === "Data") ||
      (currentTask && currentTask["taskType"] === "CombinedPolicyTask")
    ) {
      return (
        <DataPolicyTaskDetails
          handleBackClick={loadPreviousTasks}
          journeyId={journeyId}
          journeyName={journeyName}
          entityDraft={entityDraft}
          currentTask={currentTask}
          readOnly={readOnly}
          completeTask={completeTaskForJourney}
          buttonLabel={
            currentTask.name !== "Review and Submit"
              ? CONSTANTS.SAVE_CONTINUE
              : CONSTANTS.SUBMIT_APPLICATION_BUTTON
          }
        />
      );
    }
    if (
      currentTask &&
      currentTask["taskType"] === "PolicyTask" &&
      currentTask["policyRequirementType"] === "Document"
    )
      return (
        <DocumentPolicyTaskDetails
          journeyId={journeyId}
          entityDraft={entityDraft}
          currentTask={currentTask}
          readOnly={readOnly}
          completeTask={completeTaskForJourney}
          handleBackClick={loadPreviousTasks}
          deleteRequired={true}
        />
      );

    if (currentTask && currentTask["taskType"] === "ManualTask")
      return (
        <ManualTaskDetails
          journeyId={journeyId}
          entityDraft={entityDraft}
          currentTask={currentTask}
          readOnly={readOnly}
          completeTask={completeTaskForJourney}
        />
      );
  };

  const fetchJourneyTasks = () => latestJourneyTasks ? latestJourneyTasks : (initialJourneyTasks ? initialJourneyTasks : []);

  return (
    <JourneyWrapper>
      <NeoWrapper journeyId={journeyId} getMenuOptions={() => getMenuOptions(fetchJourneyTasks(), journeyId, taskId, null, taskName)}>
        {getTaskDetailsPage()}
      </NeoWrapper>
    </JourneyWrapper>
  )
};